import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useViewportCtx } from "@/contexts/ViewportCtx";
import { useCanvasStore } from "@/store/useCanvasStore";
import { useNodePageStore } from "@/store/useNodePageStore";
import { wsService, type WsMessage } from "@/services/websocket";
import { saveCanvas, saveCanvasKeepalive, fetchCanvas, CanvasConflictError } from "@/services/canvasService";
import { useIsMobile } from "@/hooks/useIsMobile";
import { ReactFlowProvider } from "@xyflow/react";
import Sidebar from "@/components/sidebar/Sidebar";
import Viewport from "@/components/canvas/Viewport";
import CanvasTopBar from "@/components/canvas/CanvasTopBar";
import FloatingToolbar from "@/components/canvas/FloatingToolbar";

const DB_SYNC_INTERVAL_MS = 1_000;

// Ticks to sit out after a save is refused as stale, doubling per consecutive
// conflict up to the cap.
//
// Two devices editing one forge each invalidate the other's base once a second.
// Retrying on the very next tick turns that into a save-storm neither side
// wins: every save conflicts, every conflict triggers a refetch, and the pair
// generate two requests a second indefinitely. Sitting out a tick lets one
// side's save land so the other can reconcile onto it.
const CONFLICT_BACKOFF_MAX_TICKS = 8;

interface Props {
  onProjectsLoaded?: () => void;
}

// True when the in-memory canvas holds edits the server has not confirmed.
//
// Every save path must consult this. The 1s interval always did; the
// forge-switch flush and the beforeunload keepalive did not, so an untouched
// tab re-POSTed its entire canvas on every switch and every close. Harmless
// since deletes became scoped to known_ids — before that, those writes were how
// an idle tab could erase rows the MCP had just inserted.
//
// Module-level so the effects can call it without carrying it in a dep array.
function hasUnsavedEdits(): boolean {
  const { revision, syncedRevision } = useCanvasStore.getState();
  return revision !== syncedRevision;
}

export default function AppShell({ onProjectsLoaded }: Props) {
  const isMobile = useIsMobile();

  const { forgeId }  = useViewportCtx();
  const { getToken } = useAuth();
  const { loadCanvas, applyWsMessage } = useCanvasStore();

  // canSaveRef: true only after initial loadCanvas completes, preventing
  // the load itself from triggering a spurious write.
  const canSaveRef  = useRef(false);
  // Cached token for synchronous beforeunload flush.
  const tokenRef    = useRef<string | null>(null);

  // Keep token cache fresh so beforeunload always has a valid token.
  useEffect(() => {
    let active = true;
    const refresh = () => {
      getToken().then((t) => { if (active) tokenRef.current = t; }).catch(() => {});
    };
    refresh();
    const id = setInterval(refresh, 55_000);
    return () => { active = false; clearInterval(id); };
  }, [getToken]);

  // Ticks still to skip before the next save attempt. A ref, not state: the
  // interval reads it and nothing renders off it.
  const conflictBackoffRef = useRef(0);
  // True while a save is in flight. The hub broadcasts canvas_revision to the
  // whole room including the client that caused it, and that frame can beat the
  // HTTP response back. Without this guard every save would race itself into a
  // refetch of a canvas this client already holds — once a second, per device.
  const savingRef = useRef(false);

  // Re-read the server's canvas and merge it under the local edits. Only ever
  // called after a 409, and deliberately not a plain reload: the unsaved edits
  // this client is holding exist nowhere else.
  const reconcile = useCallback(async (targetForgeId: string) => {
    try {
      const payload = await fetchCanvas(targetForgeId, getToken);
      // The forge may have been switched while the fetch was in flight; applying
      // another forge's canvas would be worse than skipping the reconcile.
      if (useCanvasStore.getState().forgeId !== targetForgeId) return;
      useCanvasStore.getState().reconcileServerCanvas(payload);
    } catch (err: unknown) {
      // Nothing to recover with. The edits stay unsaved and the next tick tries
      // again — the same base will be refused, but the backoff keeps that cheap.
      const reason = err instanceof Error ? err.message : "reconcile failed";
      console.warn("[taitora save] reconcile after conflict failed:", reason);
    }
  }, [getToken]);

  const getCanvasSnapshot = () => {
    const { rfNodes, rfEdges, shapes, knownIds, serverRevision } = useCanvasStore.getState();
    const nodes  = rfNodes.map((n) => n.data);
    const edges  = rfEdges.map((e) => e.data).filter((d): d is NonNullable<typeof d> => d != null);
    return {
      nodes,
      edges,
      shapes,
      // What this client claims authority to delete. Deletion is stated, not
      // inferred from absence, so rows written behind this tab's back survive.
      knownIds: Array.from(knownIds),
      // What the request actually carries — the new scope once it succeeds.
      sentIds: [...nodes.map((n) => n.id), ...edges.map((e) => e.id), ...shapes.map((s) => s.id)],
      // The forge revision this payload was built on. The server refuses the
      // save if anything landed since.
      baseRevision: serverRevision,
    };
  };

  const flushSave = useCallback((targetForgeId: string) => {
    const { nodes, edges, shapes, knownIds, sentIds, baseRevision } = getCanvasSnapshot();
    // Capture the revision *before* the request: anything the user types while
    // it is in flight must stay unsaved, or we would clear the flag on work the
    // server never received.
    const inFlight = useCanvasStore.getState().revision;
    savingRef.current = true;
    saveCanvas(targetForgeId, nodes, edges, shapes, knownIds, baseRevision, getToken)
      .then((serverRevision) => {
        conflictBackoffRef.current = 0;
        useCanvasStore.getState().markSaved(inFlight, sentIds, serverRevision);
      })
      .catch((err: unknown) => {
        // A refused save is not a failed one. The request was fine; this client
        // is simply behind, and it recovers on its own — so no save-error
        // banner, and no markSaved either, because the edits are still unsaved.
        if (err instanceof CanvasConflictError) {
          conflictBackoffRef.current = Math.min(
            conflictBackoffRef.current === 0 ? 1 : conflictBackoffRef.current * 2,
            CONFLICT_BACKOFF_MAX_TICKS,
          );
          void reconcile(targetForgeId);
          return;
        }
        // Previously swallowed outright, which is how a failing autosave stayed
        // invisible while edits piled up in memory only.
        const reason = err instanceof Error ? err.message : "save failed";
        console.warn("[taitora save] canvas save failed:", reason);
        useCanvasStore.getState().recordSaveFailure(reason);
      })
      .finally(() => { savingRef.current = false; });
  }, [getToken, reconcile]);

  // Another device advanced this forge. No patch came with the frame — that is
  // the point of it — so the only way to catch up is to read the canvas and
  // merge it under whatever is unsaved here.
  const onRevisionFrame = useCallback((msg: WsMessage) => {
    if (msg.type !== "canvas_revision") return;
    if (!forgeId || msg.forge_id !== forgeId) return;
    // Our own save, racing its response back to us.
    if (savingRef.current) return;
    const stated = (msg.payload as { revision?: number } | undefined)?.revision ?? msg.revision ?? 0;
    if (stated <= useCanvasStore.getState().serverRevision) return;
    void reconcile(forgeId);
  }, [forgeId, reconcile]);

  // ── Forge-switch: save previous, load new, connect WS ──────────────────────
  useEffect(() => {
    if (!forgeId) return;

    canSaveRef.current = false;

    // Cache + flush for the forge we're leaving. The cache is written either
    // way — it holds the in-memory edits and is what makes coming back instant.
    // Only the network write is conditional.
    const prev = useCanvasStore.getState();
    if (prev.forgeId && prev.forgeId !== forgeId) {
      useCanvasStore.getState().cacheCurrentCanvas();
      if (hasUnsavedEdits()) flushSave(prev.forgeId);
    }

    wsService.removeHandler(applyWsMessage);
    wsService.disconnect();

    loadCanvas(forgeId, getToken).then(() => {
      canSaveRef.current = true;
    }).catch(() => {});

    // Purely an optimisation now — badge counts arrive inline with the canvas,
    // so this only pre-warms the drawer's page list.
    useNodePageStore.getState().prefetchForge(forgeId, getToken).catch(() => {});

    wsService.addHandler(applyWsMessage);
    wsService.addHandler(onRevisionFrame);
    // A dropped socket replays nothing, so whatever landed while it was down is
    // invisible until something asks. The container sleeps after 5m idle, so
    // this fires on ordinary quiet, not just on deploys.
    const onReopen = () => { void reconcile(forgeId); };
    wsService.onReopen(onReopen);
    wsService.onAuthError((reason) => {
      console.warn("[taitora ws] auth error:", reason);
    });
    // Pass the getter, not a token: Clerk JWTs expire in about a minute, so a
    // token captured here would already be stale on the first reconnect.
    wsService.connect(forgeId, getToken).catch(() => {});

    return () => {
      wsService.removeHandler(applyWsMessage);
      wsService.removeHandler(onRevisionFrame);
      wsService.offReopen(onReopen);
      wsService.disconnect();
    };
  }, [forgeId, loadCanvas, getToken, applyWsMessage, flushSave, onRevisionFrame, reconcile]);

  // ── Periodic DB sync ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!forgeId) return;
    const id = setInterval(() => {
      if (!canSaveRef.current) return;
      if (conflictBackoffRef.current > 0) {
        conflictBackoffRef.current -= 1;
        return;
      }
      // Nothing new to send. Without this the tab re-POSTed the entire canvas
      // every tick forever, and two idle tabs on one forge were enough to
      // deadlock concurrent full-replace transactions against each other.
      if (!hasUnsavedEdits()) return;
      flushSave(forgeId);
    }, DB_SYNC_INTERVAL_MS);
    return () => clearInterval(id);
  }, [forgeId, flushSave]);

  // ── Beforeunload: flush to DB so refresh never loses edits ─────────────────
  useEffect(() => {
    if (!forgeId) return;
    const handle = () => {
      if (!canSaveRef.current || !tokenRef.current) return;
      if (!hasUnsavedEdits()) return;
      const { nodes, edges, shapes, knownIds, baseRevision } = getCanvasSnapshot();
      saveCanvasKeepalive(forgeId, nodes, edges, shapes, knownIds, baseRevision, tokenRef.current);
    };
    window.addEventListener("beforeunload", handle);
    return () => window.removeEventListener("beforeunload", handle);
  }, [forgeId]);

  return (
    // ReactFlowProvider hoists the React Flow store above both the canvas and the
    // floating tool bar so the bar (a fixed overlay, outside <ReactFlow>) can still
    // call useReactFlow() for screen→flow node placement.
    <ReactFlowProvider>
      <div className="flex flex-col w-screen h-screen overflow-hidden bg-canvas-bg select-none">
        {/* Top bar (desktop): the taitora.ai logo + workspace/forge breadcrumb sits
            ABOVE the menu so the rail never hides it — matching the mock, where the
            rail starts beneath the logo. */}
        {!isMobile && (
          <div className="h-[58px] shrink-0 flex items-center px-5 z-50">
            <CanvasTopBar />
          </div>
        )}

        <div className="flex flex-1 min-h-0">
          {/* Sidebar: Rail always visible; Tree collapsible */}
          <Sidebar isMobile={isMobile} onProjectsLoaded={onProjectsLoaded} />

          {/* Canvas: flex-1 so it fills remaining width after Rail */}
          <div className="flex-1 overflow-hidden">
            <Viewport isMobile={isMobile} />
          </div>
        </div>
      </div>

      {/* Desktop tool bar: a draggable fixed overlay defaulting to top-center over
          the canvas. Mobile keeps its compact in-canvas bar (in Viewport). */}
      {!isMobile && <FloatingToolbar />}
    </ReactFlowProvider>
  );
}
